<template>
  <div>
    <div class="search-area">
      <form class="search-form" v-on:submit.prevent="doSearch">
        <div class="form-group">
          <label class="form-control-label group2">事業所</label>
          <select class="form-control" v-model="state.search.parentId">
            <option :value="null"></option>
            <option v-for="e in groups2" :key="e.id" :value="e.id">
              {{e.name}}
            </option>
          </select>
        </div>
        <div class="form-group search-btn ta-center">
          <button type="submit" class="btn btn-primary">検索</button>
        </div>
      </form>
    </div>
    <AdminListTable
      :display-data="displayData"
      :fields="fields"
      @start-edit="edit"
      @start-destroy="tryDestroy"
    />
    <Modal
      v-if="state.showDestroyModal"
      :title="destroyModalTitle"
      :show-bottom-dismiss-button="true"
      @close="doDestroy"
      @dismiss="state.showDestroyModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p1">事業所名<span class="colon"></span>{{getGroup2Name(state.destroyingGroup3?.parentId ?? null)}}</p>
          <p class="p1">部署名<span class="colon"></span>{{state.destroyingGroup3?.name}}</p>
          <p class="p2 font-mtx-desc" v-html="destroyModalMsg"></p>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import type { AdminListTableField } from 'src/models/adminListTable';
import type { AdminGroup2 } from 'src/models/group2';
import type { AdminGroup3 } from 'src/models/group3';

type Group3Disp = AdminGroup3 & {
  g2Name: string;
};

interface SearchParam {
  parentId: number | null;
}

interface Group3TableState {
  search: SearchParam;
  // 検索ボタン押下時に確定する検索条件。フィルタはこちらのみを参照する（入力中は自動検索しない）
  applied: SearchParam;
  showDestroyModal: boolean;
  destroyingGroup3: AdminGroup3 | null;
}

const initSearchParam = (): SearchParam => ({
  parentId: null,
});

const props = defineProps<{
  groups3: AdminGroup3[];
  groups2: AdminGroup2[];
}>();

const emit = defineEmits<{
  (e: 'edit', group3: AdminGroup3): void;
  (e: 'destroy', group3: AdminGroup3): void;
  (e: 'search'): void;
}>();

const state = reactive<Group3TableState>({
  search: initSearchParam(),
  applied: initSearchParam(),
  showDestroyModal: false,
  destroyingGroup3: null,
});

const doSearch = (): void => {
  emit('search');
  state.applied = { ...state.search };
};

const group2Map = computed<Map<number, AdminGroup2>>(() => {
  return new Map(props.groups2.map(e => [e.id, e]));
});

const getGroup2Name = (group2Id: number | null): string => {
  if (group2Id == null) return '-';

  return group2Map.value.get(group2Id)?.name ?? '-';
};

const filteredGroups3 = computed<AdminGroup3[]>(() => {
  return props.groups3.filter(group3 => {
    if (state.applied.parentId == null || state.applied.parentId.toString() === '') return true;

    return group3.parentId === state.applied.parentId;
  });
});

const displayData = computed<Group3Disp[]>(() => {
  return filteredGroups3.value.map(g3 => {
    return {
      ...g3,
      g2Name: getGroup2Name(g3.parentId),
    };
  });
});

const edit = (obj: AdminGroup3) => {
  emit('edit', obj);
};

const tryDestroy = (obj: AdminGroup3) => {
  state.destroyingGroup3 = obj;
  state.showDestroyModal = true;
};

const doDestroy = () => {
  if (!state.destroyingGroup3) return;

  emit('destroy', state.destroyingGroup3);
  state.showDestroyModal = false;
};

const destroyModalTitle = '削除';
const destroyModalMsg = 'を削除してもよろしいですか？';
const fields: AdminListTableField<Group3Disp>[] = [
  { name: 'g2Name', label: '事業所名' },
  { name: 'name', label: '部署名' },
  { name: 'dispOrder', label: '表示順' },
];
</script>

<style lang="scss" scoped>

</style>
